import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import './SuppliersManager.css';
import AppIcon from './icons/AppIcon';

const emptyForm = {
  nome_fantasia: '',
  razao_social: '',
  cnpj: '',
  email: '',
  telefone: '',
  segmento_atendido: '',
  prazo_pagamento_dias: '',
  projetos_ids: '',
  imagem_url: '',
  endereco: '',
  status: 'ATIVO',
};

const emptyProductForm = {
  nome: '',
  sku: '',
  unidade: '',
  preco_atual: '',
  descricao: '',
};

const SuppliersManager = () => {
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fornecedores, setFornecedores] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('suppliersViewMode') || 'grid';
  });
  const [filtros, setFiltros] = useState({
    busca: '',
    status: 'ATIVO',
    segmento: '',
  });
  const [showModal, setShowModal] = useState(false);
  const [fornecedorEditando, setFornecedorEditando] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [addressForm, setAddressForm] = useState({
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
  });
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [priceEdits, setPriceEdits] = useState({});
  const [priceHistory, setPriceHistory] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('suppliersViewMode', viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (companyId) {
      loadFornecedores(companyId);
    }
  }, [filtros]);

  const normalizedProjetos = useMemo(() => {
    if (!form.projetos_ids.trim()) return [];
    return form.projetos_ids
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }, [form.projetos_ids]);

  const filteredSupplierProducts = useMemo(() => {
    if (!productSearch.trim()) return supplierProducts;
    const term = productSearch.toLowerCase();
    return supplierProducts.filter((produto) =>
      [produto.nome, produto.sku, produto.descricao]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [productSearch, supplierProducts]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.company_id) {
        setError('Usuário não vinculado a empresa');
        return;
      }

      setCompanyId(profile.company_id);
      await loadFornecedores(profile.company_id);
    } catch (err) {
      console.error('Erro ao carregar fornecedores:', err);
      setError('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal && fornecedorEditando?.id) {
      loadSupplierProducts(fornecedorEditando.id);
    }
  }, [showModal, fornecedorEditando?.id, companyId]);

  const handleSupplierImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!companyId) {
      setError('Empresa não encontrada para upload.');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('A imagem deve ter no máximo 5MB.');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Arquivo inválido. Envie uma imagem.');
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const fileName = `${companyId}/${Date.now()}-${Math.random().toString(36).slice(2, 9)}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('suppliers-images')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('suppliers-images')
        .getPublicUrl(fileName);

      setForm({ ...form, imagem_url: publicUrl || '' });
      setSuccess('✅ Imagem enviada com sucesso!');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error('Erro ao enviar imagem do fornecedor:', err);
      setError(`Erro ao enviar imagem: ${err.message}`);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const loadFornecedores = async (cid) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('fornecedores')
        .select('*')
        .eq('company_id', cid);

      if (filtros.status) {
        query = query.eq('status', filtros.status);
      }

      if (filtros.segmento) {
        query = query.eq('segmento_atendido', filtros.segmento);
      }

      if (filtros.busca) {
        query = query.or(`nome_fantasia.ilike.%${filtros.busca}%,razao_social.ilike.%${filtros.busca}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setFornecedores(data || []);
    } catch (err) {
      console.error('Erro ao carregar fornecedores:', err);
      setError('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoFornecedor = () => {
    setForm(emptyForm);
    setAddressForm({
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
    });
    setFornecedorEditando(null);
    setSupplierProducts([]);
    setProductSearch('');
    setProductForm(emptyProductForm);
    setPriceEdits({});
    setPriceHistory({});
    setExpandedHistoryId(null);
    setShowModal(true);
  };

  const handleEditarFornecedor = (fornecedor) => {
    const endereco = fornecedor.endereco || {};
    setForm({
      nome_fantasia: fornecedor.nome_fantasia || '',
      razao_social: fornecedor.razao_social || '',
      cnpj: fornecedor.cnpj || '',
      email: fornecedor.email || '',
      telefone: fornecedor.telefone || '',
      segmento_atendido: fornecedor.segmento_atendido || '',
      prazo_pagamento_dias: fornecedor.prazo_pagamento_dias ?? '',
      projetos_ids: Array.isArray(fornecedor.projetos)
        ? fornecedor.projetos.join(', ')
        : '',
      imagem_url: fornecedor.imagem_url || '',
      status: fornecedor.status || 'ATIVO',
    });
    setAddressForm({
      logradouro: endereco.logradouro || '',
      numero: endereco.numero || '',
      complemento: endereco.complemento || '',
      bairro: endereco.bairro || '',
      cidade: endereco.cidade || '',
      estado: endereco.estado || '',
      cep: endereco.cep || '',
    });
    setFornecedorEditando(fornecedor);
    setSupplierProducts([]);
    setProductSearch('');
    setProductForm(emptyProductForm);
    setPriceEdits({});
    setPriceHistory({});
    setExpandedHistoryId(null);
    setShowModal(true);
  };

  const loadPriceHistory = async (produtoId) => {
    if (!companyId || !fornecedorEditando?.id) return;
    try {
      setHistoryLoadingId(produtoId);
      const { data, error } = await supabase
        .from('produtos_fornecedor_precos')
        .select('id, preco, data, observacao')
        .eq('company_id', companyId)
        .eq('fornecedor_id', fornecedorEditando.id)
        .eq('produto_fornecedor_id', produtoId)
        .order('data', { ascending: false });

      if (error) throw error;
      setPriceHistory((prev) => ({ ...prev, [produtoId]: data || [] }));
    } catch (err) {
      console.error('Erro ao carregar histórico de preços:', err);
      setError('Erro ao carregar histórico de preços');
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const handleTogglePriceHistory = async (produtoId) => {
    if (expandedHistoryId === produtoId) {
      setExpandedHistoryId(null);
      return;
    }

    setExpandedHistoryId(produtoId);
    if (!priceHistory[produtoId]) {
      await loadPriceHistory(produtoId);
    }
  };

  const loadSupplierProducts = async (fornecedorId) => {
    if (!companyId || !fornecedorId) return;
    try {
      setProductsLoading(true);
      const { data, error } = await supabase
        .from('produtos_fornecedor')
        .select('*')
        .eq('company_id', companyId)
        .eq('fornecedor_id', fornecedorId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSupplierProducts(data || []);
      setPriceEdits((prev) => {
        const next = { ...prev };
        (data || []).forEach((produto) => {
          if (next[produto.id] === undefined) {
            next[produto.id] = String(produto.preco_atual ?? '');
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Erro ao carregar produtos do fornecedor:', err);
      setError('Erro ao carregar produtos do fornecedor');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleAddSupplierProduct = async () => {
    if (!fornecedorEditando?.id) {
      setError('Salve o fornecedor antes de adicionar produtos.');
      return;
    }

    if (!productForm.nome.trim() || !productForm.preco_atual || !productForm.unidade.trim()) {
      setError('Preencha nome, unidade e preço para adicionar o produto.');
      return;
    }

    try {
      setError(null);
      const payload = {
        company_id: companyId,
        fornecedor_id: fornecedorEditando.id,
        nome: productForm.nome.trim(),
        sku: productForm.sku.trim() || null,
        unidade: productForm.unidade.trim(),
        descricao: productForm.descricao.trim() || null,
        preco_atual: Number(productForm.preco_atual),
        ativo: true,
      };

      const { data, error } = await supabase
        .from('produtos_fornecedor')
        .insert(payload)
        .select('id')
        .single();

      if (error) throw error;

      await supabase
        .from('produtos_fornecedor_precos')
        .insert({
          company_id: companyId,
          fornecedor_id: fornecedorEditando.id,
          produto_fornecedor_id: data?.id,
          preco: Number(productForm.preco_atual),
        });

      setSuccess('Produto do fornecedor adicionado!');
      setProductForm(emptyProductForm);
      loadSupplierProducts(fornecedorEditando.id);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error('Erro ao adicionar produto do fornecedor:', err);
      setError('Erro ao adicionar produto do fornecedor');
    }
  };

  const handleRemoveSupplierProduct = async (produtoId) => {
    try {
      setError(null);
      await supabase
        .from('produtos_fornecedor')
        .delete()
        .eq('id', produtoId)
        .eq('company_id', companyId);

      setSuccess('Produto removido do fornecedor.');
      loadSupplierProducts(fornecedorEditando?.id);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error('Erro ao remover produto do fornecedor:', err);
      setError('Erro ao remover produto do fornecedor');
    }
  };

  const handleUpdateSupplierPrice = async (produto) => {
    const editedPrice = Number(priceEdits[produto.id]);
    if (Number.isNaN(editedPrice) || editedPrice <= 0) {
      setError('Informe um preço válido para atualizar.');
      return;
    }

    if (editedPrice === Number(produto.preco_atual)) {
      setError('O preço já está atualizado.');
      return;
    }

    try {
      setError(null);
      const { error } = await supabase
        .from('produtos_fornecedor')
        .update({ preco_atual: editedPrice, updated_at: new Date().toISOString() })
        .eq('id', produto.id)
        .eq('company_id', companyId);

      if (error) throw error;

      await supabase
        .from('produtos_fornecedor_precos')
        .insert({
          company_id: companyId,
          fornecedor_id: fornecedorEditando?.id,
          produto_fornecedor_id: produto.id,
          preco: editedPrice,
          observacao: 'Atualização manual no modal do fornecedor',
        });

      setSuccess('Preço atualizado com sucesso!');
      loadSupplierProducts(fornecedorEditando?.id);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      console.error('Erro ao atualizar preço do fornecedor:', err);
      setError('Erro ao atualizar preço do fornecedor');
    }
  };

  const handleSalvarFornecedor = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);

      if (!companyId) throw new Error('Empresa não encontrada');

      const enderecoJson = {
        logradouro: addressForm.logradouro || null,
        numero: addressForm.numero || null,
        complemento: addressForm.complemento || null,
        bairro: addressForm.bairro || null,
        cidade: addressForm.cidade || null,
        estado: addressForm.estado || null,
        cep: addressForm.cep || null,
      };

      const payload = {
        company_id: companyId,
        contato_id: fornecedorEditando?.contato_id || null,
        nome_fantasia: form.nome_fantasia,
        razao_social: form.razao_social || null,
        cnpj: form.cnpj || null,
        email: form.email || null,
        telefone: form.telefone || null,
        segmento_atendido: form.segmento_atendido || null,
        prazo_pagamento_dias: form.prazo_pagamento_dias ? Number(form.prazo_pagamento_dias) : null,
        projetos: normalizedProjetos.length ? normalizedProjetos : null,
        catalogo_json: fornecedorEditando?.catalogo_json ?? null,
        catalogo_arquivo_url: fornecedorEditando?.catalogo_arquivo_url ?? null,
        imagem_url: form.imagem_url || null,
        endereco: enderecoJson,
        status: form.status || 'ATIVO',
        updated_at: new Date().toISOString(),
      };

      let contatoId = fornecedorEditando?.contato_id || null;

      if (!contatoId) {
        const { data: contatoCriado, error: contatoError } = await supabase
          .from('contatos')
          .insert({
            owner_id: user?.id || null,
            company_id: companyId,
            nome: form.nome_fantasia,
            email: form.email || null,
            numero: form.telefone || null,
            canal: 'manual',
            fonte_do_contato: 'fornecedor',
            status: 'ATIVO',
            tipo: 'fornecedor',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select('id')
          .single();

        if (contatoError) throw contatoError;
        contatoId = contatoCriado?.id || null;
        payload.contato_id = contatoId;
      } else {
        await supabase
          .from('contatos')
          .update({
            nome: form.nome_fantasia,
            email: form.email || null,
            numero: form.telefone || null,
            tipo: 'fornecedor',
            updated_at: new Date().toISOString(),
          })
          .eq('id', contatoId)
          .eq('company_id', companyId);
      }

      if (fornecedorEditando?.id) {
        const { error } = await supabase
          .from('fornecedores')
          .update(payload)
          .eq('id', fornecedorEditando.id)
          .eq('company_id', companyId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('fornecedores')
          .insert(payload);

        if (error) throw error;
      }

      setSuccess('Fornecedor salvo com sucesso!');
      setShowModal(false);
      setForm(emptyForm);
      loadFornecedores(companyId);
    } catch (err) {
      console.error('Erro ao salvar fornecedor:', err);
      setError('Erro ao salvar fornecedor. Verifique os dados JSON.');
    }
  };

  if (loading) {
    return (
      <div className="suppliers-manager">
        <LoadingSpinner message="Carregando fornecedores..." />
      </div>
    );
  }

  return (
    <div className="suppliers-manager">
      <div className="suppliers-header-bar">
        <div>
          <h2>Fornecedores</h2>
          <p>Gerencie fornecedores e seus catálogos.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn-icon view-toggle"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            title={viewMode === 'grid' ? 'Visualizar em lista' : 'Visualizar em cards'}
          >
            <AppIcon name={viewMode === 'grid' ? 'view_list' : 'grid_view'} />
          </button>
          <button className="btn-secondary" type="button" onClick={loadData}>
            <AppIcon name="refresh" />
            Atualizar
          </button>
          <button className="btn-primary" type="button" onClick={handleNovoFornecedor}>
            <AppIcon name="add" />
            Novo Fornecedor
          </button>
        </div>
      </div>

      <div className="suppliers-filters">
        <div className="filter-group">
          <AppIcon name="search" />
          <input
            type="text"
            placeholder="Buscar fornecedor..."
            value={filtros.busca}
            onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
          />
        </div>
        <input
          type="text"
          placeholder="Segmento atendido"
          value={filtros.segmento}
          onChange={(e) => setFiltros({ ...filtros, segmento: e.target.value })}
        />
        <select
          value={filtros.status}
          onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
        >
          <option value="ATIVO">Ativo</option>
          <option value="INATIVO">Inativo</option>
        </select>
      </div>

      {error && (
        <div className="alert alert-error">
          <AppIcon name="error" />
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <AppIcon name="check_circle" />
          {success}
        </div>
      )}

      {fornecedores.length === 0 ? (
        <div className="empty-state">
          <AppIcon name="local_shipping" />
          <h3>Nenhum fornecedor cadastrado</h3>
          <p>Clique em "Novo Fornecedor" para começar</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="suppliers-grid">
          {fornecedores.map((fornecedor) => (
            <div key={fornecedor.id} className="supplier-card">
              <div className="supplier-card-header">
                <div>
                  <h3>{fornecedor.nome_fantasia}</h3>
                  <p>{fornecedor.razao_social || 'Sem razão social'}</p>
                </div>
                <span className={`status-badge status-${(fornecedor.status || 'ativo').toLowerCase()}`}>
                  {fornecedor.status || 'ATIVO'}
                </span>
              </div>

              <div className="supplier-card-body">
                <div className="supplier-metrics">
                  <div>
                    <span>Segmento</span>
                    <strong>{fornecedor.segmento_atendido || '-'}</strong>
                  </div>
                  <div>
                    <span>Prazo (dias)</span>
                    <strong>{fornecedor.prazo_pagamento_dias ?? '-'}</strong>
                  </div>
                  <div>
                    <span>Projetos</span>
                    <strong>{Array.isArray(fornecedor.projetos) ? fornecedor.projetos.length : 0}</strong>
                  </div>
                </div>

                <div className="supplier-meta">
                  {fornecedor.email && <span>{fornecedor.email}</span>}
                  {fornecedor.telefone && <span>{fornecedor.telefone}</span>}
                </div>
              </div>

              <div className="supplier-actions">
                <button className="btn-icon" onClick={() => handleEditarFornecedor(fornecedor)} title="Editar">
                  <AppIcon name="edit" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="suppliers-list">
          <div className="suppliers-list-header">
            <span>Fornecedor</span>
            <span>Segmento</span>
            <span>Prazo</span>
            <span>Status</span>
            <span>Ações</span>
          </div>
          {fornecedores.map((fornecedor) => (
            <div key={fornecedor.id} className="suppliers-list-row">
              <div className="suppliers-list-main">
                <strong>{fornecedor.nome_fantasia}</strong>
                <span>{fornecedor.razao_social || '-'}</span>
              </div>
              <span>{fornecedor.segmento_atendido || '-'}</span>
              <span>{fornecedor.prazo_pagamento_dias ?? '-'}</span>
              <span className={`status-badge status-${(fornecedor.status || 'ativo').toLowerCase()}`}>
                {fornecedor.status || 'ATIVO'}
              </span>
              <div className="supplier-actions">
                <button className="btn-icon" onClick={() => handleEditarFornecedor(fornecedor)} title="Editar">
                  <AppIcon name="edit" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content supplier-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{fornecedorEditando ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <AppIcon name="close" />
              </button>
            </div>

            <form className="modal-body" onSubmit={handleSalvarFornecedor}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome fantasia *</label>
                  <input
                    type="text"
                    value={form.nome_fantasia}
                    onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Razão social</label>
                  <input
                    type="text"
                    value={form.razao_social}
                    onChange={(e) => setForm({ ...form, razao_social: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>CNPJ</label>
                  <input
                    type="text"
                    value={form.cnpj}
                    onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Segmento atendido</label>
                  <input
                    type="text"
                    value={form.segmento_atendido}
                    onChange={(e) => setForm({ ...form, segmento_atendido: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="text"
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prazo pagamento (dias)</label>
                  <input
                    type="number"
                    value={form.prazo_pagamento_dias}
                    onChange={(e) => setForm({ ...form, prazo_pagamento_dias: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="ATIVO">Ativo</option>
                    <option value="INATIVO">Inativo</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Projetos (IDs separados por vírgula)</label>
                <input
                  type="text"
                  value={form.projetos_ids}
                  onChange={(e) => setForm({ ...form, projetos_ids: e.target.value })}
                />
              </div>

              <div className="form-group supplier-products-section">
                <div className="section-header">
                  <h4>Produtos do fornecedor</h4>
                  <p>Cadastre o catálogo do fornecedor para facilitar buscas e histórico de preços.</p>
                </div>

                {!fornecedorEditando?.id ? (
                  <div className="inline-alert">
                    Salve o fornecedor para liberar o cadastro de produtos.
                  </div>
                ) : (
                  <>
                    <div className="supplier-products-toolbar">
                      <div className="filter-group">
                        <AppIcon name="search" />
                        <input
                          type="text"
                          placeholder="Buscar produto no fornecedor..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="supplier-products-form">
                      <input
                        type="text"
                        placeholder="Nome do produto *"
                        value={productForm.nome}
                        onChange={(e) => setProductForm({ ...productForm, nome: e.target.value })}
                      />
                      <input
                        type="text"
                        placeholder="SKU"
                        value={productForm.sku}
                        onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                      />
                      <input
                        type="text"
                        placeholder="Unidade *"
                        value={productForm.unidade}
                        onChange={(e) => setProductForm({ ...productForm, unidade: e.target.value })}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Preço atual *"
                        value={productForm.preco_atual}
                        onChange={(e) => setProductForm({ ...productForm, preco_atual: e.target.value })}
                      />
                      <input
                        type="text"
                        placeholder="Descrição"
                        value={productForm.descricao}
                        onChange={(e) => setProductForm({ ...productForm, descricao: e.target.value })}
                      />
                      <button type="button" className="btn-primary" onClick={handleAddSupplierProduct}>
                        <AppIcon name="add" />
                        Adicionar
                      </button>
                    </div>

                    <div className="supplier-products-list">
                      {productsLoading ? (
                        <div className="inline-alert">Carregando produtos do fornecedor...</div>
                      ) : filteredSupplierProducts.length === 0 ? (
                        <div className="inline-alert">Nenhum produto cadastrado.</div>
                      ) : (
                        filteredSupplierProducts.map((produto) => (
                          <div key={produto.id} className="supplier-product-item">
                            <div className="supplier-product-row">
                              <div className="supplier-product-info">
                                <strong>{produto.nome}</strong>
                                <span>{produto.sku || 'Sem SKU'} • {produto.unidade}</span>
                              </div>
                              <div className="supplier-product-meta">
                                <div className="supplier-product-price">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={priceEdits[produto.id] ?? produto.preco_atual ?? ''}
                                    onChange={(e) =>
                                      setPriceEdits((prev) => ({
                                        ...prev,
                                        [produto.id]: e.target.value,
                                      }))
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleUpdateSupplierPrice(produto)}
                                    title="Atualizar preço"
                                  >
                                    <AppIcon name="save" />
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => handleTogglePriceHistory(produto.id)}
                                  title="Histórico de preços"
                                >
                                  <AppIcon name="history" />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon btn-danger"
                                  onClick={() => handleRemoveSupplierProduct(produto.id)}
                                  title="Remover"
                                >
                                  <AppIcon name="delete" />
                                </button>
                              </div>
                            </div>
                            {expandedHistoryId === produto.id && (
                              <div className="supplier-product-history">
                                {historyLoadingId === produto.id ? (
                                  <div className="inline-alert">Carregando histórico...</div>
                                ) : (priceHistory[produto.id] || []).length === 0 ? (
                                  <div className="inline-alert">Sem histórico registrado.</div>
                                ) : (
                                  <ul>
                                    {priceHistory[produto.id].map((item) => (
                                      <li key={item.id}>
                                        <span>R$ {Number(item.preco || 0).toFixed(2)}</span>
                                        <small>
                                          {new Date(item.data).toLocaleDateString('pt-BR')}
                                          {item.observacao ? ` • ${item.observacao}` : ''}
                                        </small>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Imagem do fornecedor</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSupplierImageUpload}
                    disabled={uploadingImage}
                  />
                  {form.imagem_url && (
                    <img src={form.imagem_url} alt="Imagem fornecedor" className="supplier-image-preview" />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Endereço</label>
                <div className="form-row">
                  <div className="form-group">
                    <label>Logradouro</label>
                    <input
                      type="text"
                      value={addressForm.logradouro}
                      onChange={(e) => setAddressForm({ ...addressForm, logradouro: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Número</label>
                    <input
                      type="text"
                      value={addressForm.numero}
                      onChange={(e) => setAddressForm({ ...addressForm, numero: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Complemento</label>
                    <input
                      type="text"
                      value={addressForm.complemento}
                      onChange={(e) => setAddressForm({ ...addressForm, complemento: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bairro</label>
                    <input
                      type="text"
                      value={addressForm.bairro}
                      onChange={(e) => setAddressForm({ ...addressForm, bairro: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Cidade</label>
                    <input
                      type="text"
                      value={addressForm.cidade}
                      onChange={(e) => setAddressForm({ ...addressForm, cidade: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Estado</label>
                    <input
                      type="text"
                      value={addressForm.estado}
                      onChange={(e) => setAddressForm({ ...addressForm, estado: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>CEP</label>
                    <input
                      type="text"
                      value={addressForm.cep}
                      onChange={(e) => setAddressForm({ ...addressForm, cep: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {fornecedorEditando ? 'Salvar Alterações' : 'Cadastrar Fornecedor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersManager;
